import React, { useEffect, useRef, useState } from 'react'

// 기간 선택기 — 프리셋(오늘/어제/최근 N일/이번달/지난달) + 두 달 달력, 둥근 디자인
const DAYS = ['일', '월', '화', '수', '목', '금', '토']

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
const parse = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d) }
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x }

const PRESETS = [
  { label: '오늘', range: () => { const t = new Date(); return [iso(t), iso(t)] } },
  { label: '어제', range: () => { const t = addDays(new Date(), -1); return [iso(t), iso(t)] } },
  { label: '최근 일주일', range: () => [iso(addDays(new Date(), -6)), iso(new Date())] },
  { label: '최근 14일', range: () => [iso(addDays(new Date(), -13)), iso(new Date())] },
  { label: '최근 30일', range: () => [iso(addDays(new Date(), -29)), iso(new Date())] },
  { label: '이번달', range: () => { const t = new Date(); return [iso(new Date(t.getFullYear(), t.getMonth(), 1)), iso(new Date(t.getFullYear(), t.getMonth() + 1, 0))] } },
  { label: '지난달', range: () => { const t = new Date(); return [iso(new Date(t.getFullYear(), t.getMonth() - 1, 1)), iso(new Date(t.getFullYear(), t.getMonth(), 0))] } },
]

function MonthGrid({ year, month, start, end, picking, onPick }) {
  const first = new Date(year, month, 1)
  const startDow = first.getDay()
  const cells = []
  for (let i = 0; i < 42; i++) {
    const d = new Date(year, month, 1 - startDow + i)
    cells.push(d)
  }
  const inRange = (s) => start && end && s >= start && s <= end
  return (
    <div className="dr-month">
      <div className="dr-month-title">{year}년 {month + 1}월</div>
      <div className="dr-grid">
        {DAYS.map((d) => <div key={d} className="dr-dow">{d}</div>)}
        {cells.map((d, i) => {
          const s = iso(d)
          const other = d.getMonth() !== month
          const isEdge = s === start || s === end
          return (
            <button
              key={i}
              type="button"
              className={`dr-day ${other ? 'other' : ''} ${inRange(s) ? 'in' : ''} ${isEdge ? 'edge' : ''}`}
              onClick={() => onPick(s)}
            >{d.getDate()}</button>
          )
        })}
      </div>
    </div>
  )
}

export default function DateRange({ start, end, onChange }) {
  const [open, setOpen] = useState(false)
  const [view, setView] = useState(() => { const d = parse(start); return [d.getFullYear(), d.getMonth()] })
  const [pickStart, setPickStart] = useState(null) // 첫 클릭 후 임시 시작일
  const ref = useRef(null)

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])

  useEffect(() => { const d = parse(start); setView([d.getFullYear(), d.getMonth()]) }, [start])

  const pick = (s) => {
    if (!pickStart) {
      setPickStart(s)
    } else {
      const [a, b] = s < pickStart ? [s, pickStart] : [pickStart, s]
      setPickStart(null)
      setOpen(false)
      onChange(a, b)
    }
  }

  const shift = (n) => setView(([y, m]) => { const d = new Date(y, m + n, 1); return [d.getFullYear(), d.getMonth()] })
  const [vy, vm] = view
  const next = new Date(vy, vm + 1, 1)
  const dispStart = pickStart ?? start
  const dispEnd = pickStart ? pickStart : end

  return (
    <div className="dr-wrap" ref={ref}>
      <button type="button" className="dr-input" onClick={() => { setOpen(!open); setPickStart(null) }}>
        <span>{start}</span>
        <span className="dr-arrow">→</span>
        <span>{end}</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
          <rect x="3" y="5" width="18" height="16" rx="3" /><path d="M3 10h18M8 3v4M16 3v4" />
        </svg>
      </button>
      {open && (
        <div className="dr-pop">
          <div className="dr-presets">
            {PRESETS.map((p) => (
              <button key={p.label} type="button" onClick={() => { const [a, b] = p.range(); setOpen(false); setPickStart(null); onChange(a, b) }}>
                {p.label}
              </button>
            ))}
          </div>
          <div className="dr-cal">
            <div className="dr-nav">
              <button type="button" onClick={() => shift(-12)}>«</button>
              <button type="button" onClick={() => shift(-1)}>‹</button>
              <span className="dr-hint muted">{pickStart ? '종료일을 선택하세요' : '시작일을 선택하세요'}</span>
              <button type="button" onClick={() => shift(1)}>›</button>
              <button type="button" onClick={() => shift(12)}>»</button>
            </div>
            <div className="dr-months">
              <MonthGrid year={vy} month={vm} start={dispStart} end={dispEnd} onPick={pick} />
              <MonthGrid year={next.getFullYear()} month={next.getMonth()} start={dispStart} end={dispEnd} onPick={pick} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// 하루만 고르는 자리 — 자금일보처럼 기간이 아니라 날짜 하나인 화면이 쓴다.
// 달력과 CSS 는 위와 그대로 나눠 쓰고, 프리셋만 하루짜리로 바꾼다.
// (기간 프리셋을 그대로 두면 "최근 30일" 을 눌렀을 때 무엇이 골라진 것인지 알 수 없다.)
const DAY_PRESETS = [
  { label: '어제', at: () => iso(addDays(new Date(), -1)) },
  { label: '오늘', at: () => iso(new Date()) },
  { label: '그제', at: () => iso(addDays(new Date(), -2)) },
  { label: '지난주 오늘', at: () => iso(addDays(new Date(), -7)) },
  { label: '지난달 말일', at: () => { const t = new Date(); return iso(new Date(t.getFullYear(), t.getMonth(), 0)) } },
]

export function DatePick({ value, onChange }) {
  const [open, setOpen] = useState(false)
  const [view, setView] = useState(() => { const d = parse(value); return [d.getFullYear(), d.getMonth()] })
  const ref = useRef(null)

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])
  useEffect(() => { const d = parse(value); setView([d.getFullYear(), d.getMonth()]) }, [value])

  const shift = (n) => setView(([y, m]) => { const d = new Date(y, m + n, 1); return [d.getFullYear(), d.getMonth()] })
  const pick = (s) => { setOpen(false); onChange(s) }
  const [vy, vm] = view
  const next = new Date(vy, vm + 1, 1)

  return (
    <div className="dr-wrap" ref={ref}>
      <button type="button" className="dr-input" onClick={() => setOpen(!open)}>
        <span>{value}</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
          <rect x="3" y="5" width="18" height="16" rx="3" /><path d="M3 10h18M8 3v4M16 3v4" />
        </svg>
      </button>
      {open && (
        <div className="dr-pop">
          <div className="dr-presets">
            {DAY_PRESETS.map((p) => (
              <button key={p.label} type="button" onClick={() => pick(p.at())}>{p.label}</button>
            ))}
          </div>
          <div className="dr-cal">
            <div className="dr-nav">
              <button type="button" onClick={() => shift(-12)}>«</button>
              <button type="button" onClick={() => shift(-1)}>‹</button>
              <span className="dr-hint muted">날짜를 선택하세요</span>
              <button type="button" onClick={() => shift(1)}>›</button>
              <button type="button" onClick={() => shift(12)}>»</button>
            </div>
            <div className="dr-months">
              {/* 시작·종료를 같은 날로 주면 그 하루만 칠해진다 */}
              <MonthGrid year={vy} month={vm} start={value} end={value} onPick={pick} />
              <MonthGrid year={next.getFullYear()} month={next.getMonth()} start={value} end={value} onPick={pick} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
